// The grocery list's saved, shared state for one week (slice 6a,
// docs/2026-10-04-groceries-design.md): the household's marks and added
// items, kept in step between phones.
//
// - Marks are applied on screen at once and queued on the phone
//   (localStorage), then sent; the queue survives a reload and is sent
//   again when the connection returns (decision 2). The server keeps the
//   newest change per line (set_grocery_mark).
// - Supabase Realtime brings in the other phones' marks and added items,
//   and a "plan changed" signal (0055's plan_change_signals) on which the
//   plan is reloaded, so the list follows plan changes (decision 1).
// - Coming back to the tab re-reads everything, so a missed message costs
//   nothing.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import {
  addGroceryExtra,
  deleteGroceryExtra,
  groceryExtraFromRow,
  groceryMarkFromRow,
  loadGroceryExtras,
  loadGroceryMarks,
  setGroceryExtraLastWeek,
  setGroceryMark,
} from "../lib/db";
import { dequeue, enqueue, marksByKey, newer, parseQueue, type Amount, type GroceryExtra, type Mark, type MarkRow } from "../lib/groceryState";

const queueKey = (householdId: string) => `wk.grocery.queue.${householdId}`;

function readQueue(householdId: string): MarkRow[] {
  try {
    return parseQueue(localStorage.getItem(queueKey(householdId)));
  } catch {
    return [];
  }
}

function writeQueue(householdId: string, queue: MarkRow[]) {
  try {
    if (queue.length) localStorage.setItem(queueKey(householdId), JSON.stringify(queue));
    else localStorage.removeItem(queueKey(householdId));
  } catch {
    // Storage unavailable (private window): the queue lives in memory only.
  }
}

// A mark row's identity within the household.
const sameLine = (a: MarkRow, b: MarkRow) => a.weekStart === b.weekStart && a.itemKey === b.itemKey;

export interface GroceryState {
  ready: boolean;
  marks: Map<string, MarkRow>; // this week's, by item key, pending changes included
  extras: GroceryExtra[]; // every added item the household has
  online: boolean;
  pending: number; // changes waiting to be sent
  saveFailed: boolean; // the last send failed while online
  setMark: (itemKey: string, mark: Mark | null, amounts: Amount[]) => void;
  addExtra: (name: string, amount?: string) => Promise<void>;
  setPinned: (extra: GroceryExtra, pinned: boolean) => Promise<void>;
  removeExtra: (extra: GroceryExtra) => Promise<void>;
}

export function useGroceryState(householdId: string, weekStart: string, onPlanChanged: () => void): GroceryState {
  const [saved, setSaved] = useState<MarkRow[]>([]);
  const [extras, setExtras] = useState<GroceryExtra[]>([]);
  const [queue, setQueue] = useState<MarkRow[]>(() => readQueue(householdId));
  const [ready, setReady] = useState(false);
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  const [saveFailed, setSaveFailed] = useState(false);
  const queueRef = useRef(queue);
  const flushing = useRef(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flushRef = useRef<() => Promise<void>>(async () => {});
  const planChangedRef = useRef(onPlanChanged);
  planChangedRef.current = onPlanChanged;

  const updateQueue = useCallback(
    (fn: (q: MarkRow[]) => MarkRow[]) => {
      queueRef.current = fn(queueRef.current);
      writeQueue(householdId, queueRef.current);
      setQueue(queueRef.current);
    },
    [householdId]
  );

  // A saved row, from a load, a send, or another phone: keep the newest.
  const absorb = useCallback((rows: MarkRow[]) => {
    setSaved((prev) => {
      const next = [...prev];
      for (const row of rows) {
        const i = next.findIndex((r) => sameLine(r, row));
        if (i === -1) next.push(row);
        else next[i] = newer(next[i], row)!;
      }
      return next;
    });
  }, []);

  // Sends queued changes one at a time; stops at the first failure (most
  // likely no connection) and leaves the rest queued. Online, it tries again
  // in a few seconds; offline, the "online" event below sends them.
  const flush = useCallback(async () => {
    if (flushing.current || !householdId) return;
    flushing.current = true;
    try {
      while (queueRef.current.length > 0) {
        const row = queueRef.current[0];
        try {
          await setGroceryMark(householdId, row);
        } catch (e) {
          console.error("[grocery] saving a mark failed:", e);
          setSaveFailed(navigator.onLine);
          clearTimeout(retryTimer.current);
          if (navigator.onLine) retryTimer.current = setTimeout(() => flushRef.current(), 10000);
          return;
        }
        setSaveFailed(false);
        absorb([row]);
        updateQueue((q) => dequeue(q, row));
      }
    } finally {
      flushing.current = false;
    }
  }, [householdId, absorb, updateQueue]);

  flushRef.current = flush;
  useEffect(() => () => clearTimeout(retryTimer.current), []);

  const reload = useCallback(async () => {
    if (!householdId) return;
    try {
      const [marks, allExtras] = await Promise.all([loadGroceryMarks(householdId, weekStart), loadGroceryExtras(householdId)]);
      setSaved(marks);
      setExtras(allExtras);
      setReady(true);
    } catch (e) {
      console.error("[grocery] loading the list's state failed:", e);
      setReady(true);
    }
  }, [householdId, weekStart]);

  // Load the week, and send anything left over from last time.
  useEffect(() => {
    setReady(false);
    setSaved([]);
    reload().then(flush);
  }, [reload, flush]);

  // Live updates from the household's other phones.
  useEffect(() => {
    if (!householdId) return undefined;
    const filter = `household_id=eq.${householdId}`;
    let planTimer: ReturnType<typeof setTimeout> | undefined;
    const channel = supabase
      .channel(`grocery-${householdId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "grocery_marks", filter }, (payload) => {
        const rec = payload.new as Record<string, unknown> | undefined;
        if (rec && rec.week_start === weekStart) absorb([groceryMarkFromRow(rec)]);
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "grocery_extras", filter }, (payload) => {
        if (payload.eventType === "DELETE") {
          const id = (payload.old as { id?: string }).id;
          setExtras((prev) => prev.filter((e) => e.id !== id));
          return;
        }
        const extra = groceryExtraFromRow(payload.new);
        setExtras((prev) => [...prev.filter((e) => e.id !== extra.id), extra]);
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "plan_change_signals", filter }, () => {
        // A bulk change (a copied week) touches the signal once per row.
        clearTimeout(planTimer);
        planTimer = setTimeout(() => planChangedRef.current(), 400);
      })
      .subscribe();
    return () => {
      clearTimeout(planTimer);
      supabase.removeChannel(channel);
    };
  }, [householdId, weekStart, absorb]);

  // Connection and tab visibility.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      reload().then(flush);
      planChangedRef.current();
    };
    const goOffline = () => setOnline(false);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      reload().then(flush);
      planChangedRef.current();
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload, flush]);

  const setMark = useCallback(
    (itemKey: string, mark: Mark | null, amounts: Amount[]) => {
      updateQueue((q) => enqueue(q, { weekStart, itemKey, mark, amounts, changedAt: new Date().toISOString() }));
      flush();
    },
    [weekStart, updateQueue, flush]
  );

  // Added items aren't queued: they need a connection (the page says so).
  const addExtra = useCallback(
    async (name: string, amount = "") => {
      const extra = await addGroceryExtra(householdId, { name, amount, firstWeek: weekStart, lastWeek: weekStart });
      setExtras((prev) => [...prev.filter((e) => e.id !== extra.id), extra]);
    },
    [householdId, weekStart]
  );

  // Pinning keeps it on every week from its first; unpinning keeps it on
  // the week being viewed and earlier ones only.
  const setPinned = useCallback(
    async (extra: GroceryExtra, pinned: boolean) => {
      const lastWeek = pinned ? null : weekStart < extra.firstWeek ? extra.firstWeek : weekStart;
      await setGroceryExtraLastWeek(extra.id, lastWeek);
      setExtras((prev) => prev.map((e) => (e.id === extra.id ? { ...e, lastWeek } : e)));
    },
    [weekStart]
  );

  const removeExtra = useCallback(async (extra: GroceryExtra) => {
    await deleteGroceryExtra(extra.id);
    setExtras((prev) => prev.filter((e) => e.id !== extra.id));
  }, []);

  const marks = useMemo(() => marksByKey(saved, queue, weekStart), [saved, queue, weekStart]);

  return { ready, marks, extras, online, pending: queue.length, saveFailed, setMark, addExtra, setPinned, removeExtra };
}
